/**
 * calcRental — single source of truth for all rental price calculations.
 *
 * Used by: BookingEngineModal, AddRentalModal, CheckInModal, InvoiceCreateModal
 *
 * @param {object} params
 * @param {number}  params.rate           - daily rate (already resolved from group if applicable)
 * @param {number}  params.days           - number of rental days
 * @param {number}  params.discount       - discount amount (number)
 * @param {string}  params.discType       - 'pct' | 'eur'
 * @param {object|null} params.selectedIns - insurance object { id, name, price } or null
 * @param {Array}   params.extrasConfig   - full extras array from context
 * @param {object}  params.selectedExtras - { [extraId]: qty }
 * @param {number}  params.locFee        - pickup + return location fee
 * @param {string}  params.vatMode       - 'inclusive' | 'exclusive'
 * @param {number}  params.vatRate       - decimal e.g. 0.19
 * @param {string}  [params.vehicleName] - for invoice line label
 *
 * @returns {object}
 *   rate, days, rawBase, discAmt, baseSub,
 *   insTotal, extraTotal, locFee,
 *   subtotal,   ← sum of all ex-VAT components
 *   vatAmt,     ← VAT amount
 *   grandTotal, ← what the customer pays
 *   vatPct,     ← e.g. 19
 *   lines[],    ← ready-to-use invoice line items
 */
export function calcRental({
  rate = 0,
  days = 1,
  discount = 0,
  discType = 'eur',
  selectedIns = null,
  extrasConfig = [],
  selectedExtras = {},
  locFee = 0,
  vatMode = 'inclusive',
  vatRate = 0.19,
  vehicleName = 'Vehicle rental',
}) {
  // ── Base vehicle cost ─────────────────────────────────────────────────────
  const rawBase  = parseFloat(rate) * days;
  const discAmt  = discType === 'pct'
    ? rawBase * (parseFloat(discount) || 0) / 100
    : (parseFloat(discount) || 0);
  const baseSub  = Math.max(0, rawBase - discAmt);

  // ── Insurance ─────────────────────────────────────────────────────────────
  const insPrice = selectedIns ? (parseFloat(selectedIns.price) || 0) : 0;
  const insTotal = insPrice * days;

  // ── Extras ────────────────────────────────────────────────────────────────
  let extraTotal = 0;
  const extraLines = [];
  extrasConfig.forEach(ex => {
    const qty = parseFloat(selectedExtras[ex.id]) || 0;
    if (qty <= 0) return;
    const unitPrice  = parseFloat(ex.price) || 0;
    const lineTotal  = (ex.perDay ? unitPrice * days : unitPrice) * qty;
    extraTotal += lineTotal;
    extraLines.push({ ex, qty, unitPrice, lineTotal, perDay: !!ex.perDay });
  });

  // ── Subtotal (all ex-VAT components) ─────────────────────────────────────
  // For VAT inclusive: prices already include VAT, so subtotal = full sum
  // For VAT exclusive: subtotal = base before VAT
  const subtotal = baseSub + insTotal + extraTotal + locFee;

  // ── VAT ───────────────────────────────────────────────────────────────────
  let vatAmt, grandTotal, subtotalExVat;
  if (vatMode === 'inclusive') {
    // VAT is already inside the prices
    vatAmt       = subtotal - subtotal / (1 + vatRate);
    grandTotal   = subtotal;                          // no change
    subtotalExVat = subtotal / (1 + vatRate);         // base without VAT
  } else {
    // VAT added on top
    vatAmt       = subtotal * vatRate;
    grandTotal   = subtotal + vatAmt;
    subtotalExVat = subtotal;
  }

  const vatPct = Math.round(vatRate * 100);

  // ── Invoice lines ─────────────────────────────────────────────────────────
  // All unit prices shown ex-VAT on the invoice
  const f = vatMode === 'inclusive' ? 1 / (1 + vatRate) : 1;

  const lines = [];

  // Vehicle line
  const baseSubExVat = baseSub * f;
  const baseUnitExVat = days > 0 ? baseSubExVat / days : 0;
  lines.push({
    desc:  `${vehicleName} (${days} day${days !== 1 ? 's' : ''})`,
    qty:   days,
    unit:  parseFloat(baseUnitExVat.toFixed(2)),
    total: parseFloat(baseSubExVat.toFixed(2)),
  });

  // Insurance line
  if (selectedIns && insTotal > 0) {
    const insExVat = insTotal * f;
    lines.push({
      desc:  `Insurance — ${selectedIns.name}`,
      qty:   days,
      unit:  parseFloat((insExVat / days).toFixed(2)),
      total: parseFloat(insExVat.toFixed(2)),
    });
  }

  // Extra lines (each individually)
  extraLines.forEach(({ ex, qty, unitPrice, lineTotal, perDay }) => {
    const lineExVat = lineTotal * f;
    const unitExVat = unitPrice * f;
    lines.push({
      desc:  ex.name + (qty > 1 ? ` ×${qty}` : ''),
      qty:   perDay ? days * qty : qty,
      unit:  parseFloat(unitExVat.toFixed(2)),
      total: parseFloat(lineExVat.toFixed(2)),
    });
  });

  // Location fee line
  if (locFee > 0) {
    const locExVat = locFee * f;
    lines.push({
      desc:  'Location fee',
      qty:   1,
      unit:  parseFloat(locExVat.toFixed(2)),
      total: parseFloat(locExVat.toFixed(2)),
    });
  }

  return {
    rate:         parseFloat(rate),
    days,
    rawBase:      parseFloat(rawBase.toFixed(2)),
    discAmt:      parseFloat(discAmt.toFixed(2)),
    baseSub:      parseFloat(baseSub.toFixed(2)),
    insTotal:     parseFloat(insTotal.toFixed(2)),
    extraTotal:   parseFloat(extraTotal.toFixed(2)),
    locFee:       parseFloat(locFee.toFixed(2)),
    subtotal:     parseFloat(subtotal.toFixed(2)),
    subtotalExVat: parseFloat(subtotalExVat.toFixed(2)),
    vatAmt:       parseFloat(vatAmt.toFixed(2)),
    grandTotal:   parseFloat(grandTotal.toFixed(2)),
    vatPct,
    vatMode,
    vatRate,
    lines,
    extraLines,   // raw extra breakdown for display purposes
  };
}

/**
 * resolveRate — gets the effective daily rate for a vehicle.
 *
 * Priority order:
 *   1. Model price        (if vehicle has modelId AND model has priceDay set)
 *   2. Group price        (if vehicle.useGroupPricing && vehicle.groupId)
 *   3. Individual price   (vehicle.priceDay)
 */
export function resolveRate(vehicle, groups = [], vehicleModels = []) {
  if (!vehicle) return 0;
  // 1) Model price wins when vehicle is assigned to a model
  if (vehicle.modelId) {
    const m = vehicleModels.find(mm => mm.id === vehicle.modelId);
    if (m && m.priceDay !== '' && m.priceDay != null) {
      return parseFloat(m.priceDay) || 0;
    }
  }
  // 2) Group pricing (legacy)
  if (vehicle.useGroupPricing && vehicle.groupId) {
    const g = groups.find(gg => gg.id === vehicle.groupId);
    if (g) return parseFloat(g.priceDay) || 0;
  }
  // 3) Individual vehicle price
  return parseFloat(vehicle.priceDay) || 0;
}
